import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import { fetchTsIds } from './shared/parallelFetch'
import { useToFill } from './shared/useToFill'

const CHART_BASE = import.meta.env.VITE_API_URL || '/api'
const dbg = import.meta.env.DEV ? console.debug.bind(console) : () => {}

// Z rowDefs extrahuje unikátní datové a normálové tsIds
export function getPeriodTsIds(rowDefs) {
  const dataTsIds = [...new Set(rowDefs.filter(r => !r.isNorm).map(r => r.tsId))]
  const normTsIds = [...new Set(rowDefs.filter(r =>  r.isNorm).map(r => r.tsId))]
  return { dataTsIds, normTsIds }
}

// mode='ROK', params={ year }
// mode='ROK_OD_DO', params={ yearFrom, yearTo, chip }
//   chip='ROK'     → &stype=YEAR
//   chip='01'–'12' → &stype=MONTH&sid={chip}
export function buildPeriodChartUrl(station, mode, params, chartType, format) {
  const base = `${CHART_BASE}/chart?type=mdata&cfg=${chartType}&station=${station}`
  if (mode === 'ROK') {
    const { year } = params
    return `${base}&from=${year}01010000&to=${year}12310000&format=${format}`
  }
  const { yearFrom, yearTo, chip } = params
  const range = `&from=${yearFrom}01010000&to=${yearTo}12310000`
  if (chip === 'ROK') return `${base}${range}&stype=YEAR&format=${format}`
  return `${base}${range}&stype=MONTH&sid=${chip}&format=${format}`
}

// Hook pro záložku Data — Režim ROK
// Volá dva dotazy: měsíční (stype=MONTH) + roční agregace (stype=YEAR)
// Vrací: { monthDataMap, yearDataMap, monthNormalsMap, yearNormal, isLoading }
//   monthDataMap[tsId][sid] = value
//   yearDataMap[tsId] = value (pro daný rok, jeden záznam)
//   monthNormalsMap[tsId][sid] = average
//   yearNormalMap[tsId] = average
export function usePeriodRokData(ghId, year, dataTsIds, normTsIds) {
  const queryKey = ['periodRokData', ghId, year, dataTsIds, normTsIds]

  const { data, isLoading, isError, error } = useQuery({
    queryKey,
    queryFn: async () => {
      dbg('[PeriodGraf/ROK] queryFn start', { ghId, year, dataTsIds, normTsIds })
      const [monthResp, yearResp, monthNormals, yearNormals] = await Promise.all([
        fetchTsIds(dataTsIds, tsId => {
          const url = `/sdata_ts?ts_id=${tsId}&gh_id=${ghId}&year_from=${year}&year_to=${year}&stype=MONTH`
          dbg('[PeriodGraf/ROK] GET', url)
          return api.get(url).then(r => { dbg('[PeriodGraf/ROK] RESP sdata_ts MONTH', tsId, r); return r })
        }),
        fetchTsIds(dataTsIds, tsId => {
          const url = `/sdata_ts?ts_id=${tsId}&gh_id=${ghId}&year_from=${year}&year_to=${year}&stype=YEAR`
          dbg('[PeriodGraf/ROK] GET', url)
          return api.get(url).then(r => { dbg('[PeriodGraf/ROK] RESP sdata_ts YEAR', tsId, r); return r })
        }),
        normTsIds.length > 0
          ? fetchTsIds(normTsIds, tsId => {
              const url = `/sndata_ts?ts_id=${tsId}&gh_id=${ghId}&stype=MONTH`
              dbg('[PeriodGraf/ROK] GET', url)
              return api.get(url).then(r => { dbg('[PeriodGraf/ROK] RESP sndata_ts MONTH', tsId, r); return r }).catch(() => ({ data: [], to_fill: [] }))
            })
          : Promise.resolve([]),
        normTsIds.length > 0
          ? fetchTsIds(normTsIds, tsId => {
              const url = `/sndata_ts?ts_id=${tsId}&gh_id=${ghId}&stype=YEAR`
              dbg('[PeriodGraf/ROK] GET', url)
              return api.get(url).then(r => { dbg('[PeriodGraf/ROK] RESP sndata_ts YEAR', tsId, r); return r }).catch(() => ({ data: [], to_fill: [] }))
            })
          : Promise.resolve([]),
      ])
      const result = {
        monthResults: monthResp.map(r => r?.data ?? []),
        yearResults: yearResp.map(r => r?.data ?? []),
        monthNormals: monthNormals.map(r => r?.data ?? []),
        yearNormals: yearNormals.map(r => r?.data ?? []),
      }
      const toFill = [...monthResp, ...yearResp, ...monthNormals, ...yearNormals]
        .flatMap(r => r?.to_fill ?? [])
      dbg('[PeriodGraf/ROK] queryFn done', result, 'to_fill:', toFill.length)
      return { result, toFill }
    },
    enabled: !!ghId && !!year && dataTsIds.length > 0,
    staleTime: 5 * 60 * 1000,
  })

  // Merge: sdata (dataTsIds) → monthResults/yearResults, sndata (normTsIds) → month/yearNormals.
  useToFill(queryKey, data, (result, resp, item) => {
    const isNorm = item.endpoint?.includes('sndata_ts')
    const ids = isNorm ? normTsIds : dataTsIds
    const i = ids.indexOf(item.params?.ts_id)
    if (i < 0) return result
    const yr = item.params?.stype === 'YEAR'
    const bucket = isNorm ? (yr ? 'yearNormals' : 'monthNormals') : (yr ? 'yearResults' : 'monthResults')
    const next = result[bucket].slice()
    next[i] = resp.data
    return { ...result, [bucket]: next }
  })

  const monthDataMap = {}
  const yearDataMap = {}
  const monthNormalsMap = {}
  const yearNormalMap = {}

  const R = data?.result
  if (R) {
    dataTsIds.forEach((tsId, i) => {
      monthDataMap[tsId] = {}
      ;(R.monthResults[i] ?? []).forEach(row => {
        if (row.sid) monthDataMap[tsId][row.sid] = row.value ?? null
      })
      const yRows = R.yearResults[i] ?? []
      yearDataMap[tsId] = yRows.length > 0 ? (yRows[0]?.value ?? null) : null
    })
    normTsIds.forEach((tsId, i) => {
      monthNormalsMap[tsId] = {}
      ;(R.monthNormals[i] ?? []).forEach(row => {
        if (row.sid) monthNormalsMap[tsId][row.sid] = row.average ?? null
      })
      const ynRows = R.yearNormals[i] ?? []
      yearNormalMap[tsId] = ynRows.length > 0 ? (ynRows[0]?.average ?? null) : null
    })
  }

  if (isError) console.error('[PeriodGraf/ROK] chyba dotazu:', error)
  return { monthDataMap, yearDataMap, monthNormalsMap, yearNormalMap, isLoading, isError }
}

// Hook pro záložku Data — Režim ROK_OD_DO
// selectedChips: pole hodnot z ['01'–'12', 'ROK']
// Vrací: { chipDataMap, chipNormalsMap, years, isLoading }
//   chipDataMap[chip][tsId][year] = value
//   chipNormalsMap[chip][tsId] = average  (stejná hodnota pro všechny roky)
export function usePeriodRokOdDoData(ghId, yearFrom, yearTo, dataTsIds, normTsIds, selectedChips) {
  const monthChips = selectedChips.filter(c => c !== 'ROK')
  const hasRok = selectedChips.includes('ROK')

  const queryKey = ['periodRokOdDoData', ghId, yearFrom, yearTo, dataTsIds, normTsIds, selectedChips]

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: async () => {
      dbg('[PeriodGraf/ROK_OD_DO] queryFn start', { ghId, yearFrom, yearTo, dataTsIds, normTsIds, selectedChips, monthChips, hasRok })
      const result = {}
      const toFill = []

      if (monthChips.length > 0) {
        const sidParam = monthChips.length === 1 ? monthChips[0] : `[${monthChips.join(',')}]`
        const [monthResp, monthNormals] = await Promise.all([
          fetchTsIds(dataTsIds, tsId => {
            const url = `/sdata_ts?ts_id=${tsId}&gh_id=${ghId}&year_from=${yearFrom}&year_to=${yearTo}&stype=MONTH&sid=${sidParam}`
            dbg('[PeriodGraf/ROK_OD_DO] GET', url)
            return api.get(url).then(r => { dbg('[PeriodGraf/ROK_OD_DO] RESP sdata_ts MONTH', tsId, r); return r })
          }),
          normTsIds.length > 0
            ? fetchTsIds(normTsIds, tsId => {
                const url = `/sndata_ts?ts_id=${tsId}&gh_id=${ghId}&stype=MONTH&sid=${sidParam}`
                dbg('[PeriodGraf/ROK_OD_DO] GET', url)
                return api.get(url).then(r => { dbg('[PeriodGraf/ROK_OD_DO] RESP sndata_ts MONTH', tsId, r); return r }).catch(() => ({ data: [], to_fill: [] }))
              })
            : Promise.resolve([]),
        ])
        result.monthData = monthResp.map(r => r?.data ?? [])
        result.monthNormals = monthNormals.map(r => r?.data ?? [])
        toFill.push(...[...monthResp, ...monthNormals].flatMap(r => r?.to_fill ?? []))
      }

      if (hasRok) {
        const [yearResp, yearNormals] = await Promise.all([
          fetchTsIds(dataTsIds, tsId => {
            const url = `/sdata_ts?ts_id=${tsId}&gh_id=${ghId}&year_from=${yearFrom}&year_to=${yearTo}&stype=YEAR`
            dbg('[PeriodGraf/ROK_OD_DO] GET', url)
            return api.get(url).then(r => { dbg('[PeriodGraf/ROK_OD_DO] RESP sdata_ts YEAR', tsId, r); return r })
          }),
          normTsIds.length > 0
            ? fetchTsIds(normTsIds, tsId => {
                const url = `/sndata_ts?ts_id=${tsId}&gh_id=${ghId}&stype=YEAR`
                dbg('[PeriodGraf/ROK_OD_DO] GET', url)
                return api.get(url).then(r => { dbg('[PeriodGraf/ROK_OD_DO] RESP sndata_ts YEAR', tsId, r); return r }).catch(() => ({ data: [], to_fill: [] }))
              })
            : Promise.resolve([]),
        ])
        result.yearData = yearResp.map(r => r?.data ?? [])
        result.yearNormals = yearNormals.map(r => r?.data ?? [])
        toFill.push(...[...yearResp, ...yearNormals].flatMap(r => r?.to_fill ?? []))
      }

      dbg('[PeriodGraf/ROK_OD_DO] queryFn done', result, 'to_fill:', toFill.length)
      return { result, toFill }
    },
    enabled: !!ghId && !!yearFrom && !!yearTo && dataTsIds.length > 0 && selectedChips.length > 0,
    staleTime: 5 * 60 * 1000,
  })

  // Merge: sdata → month/yearData, sndata → month/yearNormals.
  useToFill(queryKey, data, (result, resp, item) => {
    const isNorm = item.endpoint?.includes('sndata_ts')
    const ids = isNorm ? normTsIds : dataTsIds
    const i = ids.indexOf(item.params?.ts_id)
    if (i < 0) return result
    const yr = item.params?.stype === 'YEAR'
    const bucket = isNorm ? (yr ? 'yearNormals' : 'monthNormals') : (yr ? 'yearData' : 'monthData')
    if (!Array.isArray(result[bucket])) return result
    const next = result[bucket].slice()
    next[i] = resp.data
    return { ...result, [bucket]: next }
  })

  const chipDataMap = {}
  const chipNormalsMap = {}

  const D = data?.result
  if (D) {
    if (monthChips.length > 0 && D.monthData) {
      dataTsIds.forEach((tsId, i) => {
        const rows = D.monthData[i] ?? []
        monthChips.forEach(chip => {
          if (!chipDataMap[chip]) chipDataMap[chip] = {}
          chipDataMap[chip][tsId] = {}
          rows.filter(r => r.sid === chip).forEach(r => {
            if (r.year) chipDataMap[chip][tsId][String(r.year)] = r.value ?? null
          })
        })
      })
      normTsIds.forEach((tsId, i) => {
        const normals = D.monthNormals?.[i] ?? []
        monthChips.forEach(chip => {
          if (!chipNormalsMap[chip]) chipNormalsMap[chip] = {}
          const normalRow = normals.find(r => r.sid === chip)
          chipNormalsMap[chip][tsId] = normalRow?.average ?? null
        })
      })
    }

    if (hasRok && D.yearData) {
      dataTsIds.forEach((tsId, i) => {
        const rows = D.yearData[i] ?? []
        if (!chipDataMap['ROK']) chipDataMap['ROK'] = {}
        chipDataMap['ROK'][tsId] = {}
        rows.forEach(r => {
          if (r.year) chipDataMap['ROK'][tsId][String(r.year)] = r.value ?? null
        })
      })
      normTsIds.forEach((tsId, i) => {
        const normals = D.yearNormals?.[i] ?? []
        if (!chipNormalsMap['ROK']) chipNormalsMap['ROK'] = {}
        chipNormalsMap['ROK'][tsId] = normals.length > 0 ? (normals[0]?.average ?? null) : null
      })
    }
  }

  return { chipDataMap, chipNormalsMap, isLoading, isError }
}
